import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Access, AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import {
  AccessesFieldType,
  getAccessCategoriesScopeIds,
  getAccessesManagementOptions,
  getAccessesValues,
  getAccessLevelOptions,
  getAccessLevelShortOptions,
  getEmptyAccessesValues,
} from '@lib/utils/accessesManagement';
import Typography from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import InterventionCategoriesChipsList from '@lib/components/InterventionCategoryChipList/InterventionCategoryChipList';
import AssetCategoriesChipsList from '@lib/components/AssetCategoryChipList/AssetCategoryChipList';
import DocumentationCategoriesChipsList from '@lib/components/DocumentationCategoryChipList/DocumentationCategoryChipList';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import getAccessListItemContentClassName from 'utils/getAccessListItemContentClassName';
import styles from './TeamAccess.module.scss';

type Props = {
  accesses?: Access[];
};

function TeamAccessView(props: Props) {
  const { accesses = [] } = props;
  const { t } = useTranslation();
  const { getUserAccessByCompany, isFullAdmin }: ContextProps =
    useOutletContext();
  const isInterventionsLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.LimitedRead;
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // AI assistance is a company-plan-level module: hide the per-user/team
  // setting entirely when the company plan doesn't grant it, instead of
  // showing a toggle that can never take effect.
  const isAiAssistanceHidden =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) !==
    AccessLevelEnum.Write;

  // Intervention categories ids
  const interventionCategoriesIds = getAccessCategoriesScopeIds(
    accesses,
    AccessComponentEnum.InterventionsManagementCategoriesScope,
  );
  // Asset categories ids
  const assetCategoriesIds = getAccessCategoriesScopeIds(
    accesses,
    AccessComponentEnum.AssetsManagementCategoriesScope,
  );
  // Documentation categories ids
  const documentationCategoriesIds = getAccessCategoriesScopeIds(
    accesses,
    AccessComponentEnum.DocumentationManagementCategoriesScope,
  );
  const accessesValues = {
    ...getEmptyAccessesValues(),
    ...getAccessesValues(accesses),
  };

  return (
    <div>
      {getAccessesManagementOptions().map((option) => {
        if (
          option.value === AccessComponentEnum.AiAssistance &&
          isAiAssistanceHidden
        ) {
          return null;
        }
        const levelOptions =
          option.fieldType === AccessesFieldType.ShortOptions
            ? getAccessLevelShortOptions()
            : getAccessLevelOptions();
        return (
          <div key={option.value} className={styles.accessListItem}>
            <div
              className={getAccessListItemContentClassName(
                option.fieldType,
                styles,
                option.value === AccessComponentEnum.InterventionsManagement &&
                  isInterventionsLocked,
              )}
            >
              <div className={styles.accessListItemTitleWrap}>
                <div className={styles.accessListItemTitle}>
                  <Typography variant="body" strong>
                    {t(option.labelTransKey)}
                  </Typography>
                  {!!option.descriptionTransKey && (
                    <div className={styles.accessListItemTitleInfo}>
                      <Tooltip body={t(option.descriptionTransKey)}>
                        <MaterialIcon
                          cursor="help"
                          icon="info"
                          symbolsOutlined
                          size="md-16"
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.col}>
                <div className={styles.accessListItemField}>
                  {option.value ===
                    AccessComponentEnum.InterventionsManagement &&
                  isInterventionsLocked ? (
                    <Tooltip body={t('lock-state-title-interventions')}>
                      <Chip
                        leftIcon="lock"
                        size={ChipSizes.small}
                        type={ChipTypes.chipBlue}
                        to={APP_URLS.dashboard.interventions.index.path}
                        asBlockElement
                      />
                    </Tooltip>
                  ) : (
                    <RadioButtonGroup
                      options={levelOptions}
                      RadioButtonComponent={RadioButtonAsButton}
                      wrapView={RadioButtonWrapViews.rowNoWrap}
                      disabled
                      value={_get(accessesValues, [option.value, 'level'])}
                    />
                  )}
                </div>
              </div>
            </div>
            {option.value === AccessComponentEnum.InterventionsManagement &&
            isInterventionsLocked
              ? null
              : !!option.subOptions?.length &&
                option.subOptions.map((subOption) => {
                  const isDocumentationSubOptionLocked =
                    option.value ===
                      AccessComponentEnum.DocumentationManagement &&
                    isDocumentationLocked;
                  if (isDocumentationSubOptionLocked && !isFullAdmin)
                    return null;
                  return (
                    <div
                      key={subOption.value}
                      className={getAccessListItemContentClassName(
                        subOption.fieldType,
                        styles,
                        isDocumentationSubOptionLocked,
                      )}
                    >
                      <div className={styles.accessListItemTitleWrap}>
                        <div className={styles.accessListItemTitle}>
                          <Typography variant="body">
                            {t(subOption.labelTransKey)}
                          </Typography>
                          {!!subOption.descriptionTransKey && (
                            <div className={styles.accessListItemTitleInfo}>
                              <Tooltip body={t(subOption.descriptionTransKey)}>
                                <MaterialIcon
                                  cursor="help"
                                  icon="info"
                                  symbolsOutlined
                                  size="md-16"
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                      {isDocumentationSubOptionLocked && (
                        <div className={styles.col}>
                          <div className={styles.accessListItemField}>
                            <Tooltip body={t('lock-state-title-documentation')}>
                              <Chip
                                leftIcon="lock"
                                size={ChipSizes.small}
                                type={ChipTypes.chipBlue}
                                to={APP_URLS.dashboard.documentation.index.path}
                                asBlockElement
                              />
                            </Tooltip>
                          </div>
                        </div>
                      )}
                      {!isDocumentationSubOptionLocked &&
                        subOption.fieldType ===
                          AccessesFieldType.FullOptions && (
                          <div className={styles.col}>
                            <div className={styles.accessListItemField}>
                              <RadioButtonGroup
                                options={getAccessLevelOptions()}
                                RadioButtonComponent={RadioButtonAsButton}
                                wrapView={RadioButtonWrapViews.rowNoWrap}
                                disabled
                                value={_get(accessesValues, [
                                  subOption.value,
                                  'level',
                                ])}
                              />
                            </div>
                          </div>
                        )}
                      {!isDocumentationSubOptionLocked &&
                        subOption.fieldType ===
                          AccessesFieldType.ShortOptions && (
                          <div className={styles.col}>
                            <div className={styles.accessListItemField}>
                              <RadioButtonGroup
                                options={getAccessLevelShortOptions()}
                                RadioButtonComponent={RadioButtonAsButton}
                                wrapView={RadioButtonWrapViews.rowNoWrap}
                                disabled
                                value={_get(accessesValues, [
                                  subOption.value,
                                  'level',
                                ])}
                              />
                            </div>
                          </div>
                        )}
                      {!isDocumentationSubOptionLocked &&
                        subOption.fieldType ===
                          AccessesFieldType.InterventionCategoriesSelect && (
                          <div>
                            {interventionCategoriesIds?.length ? (
                              <div className={styles.accessListItemField}>
                                <InterventionCategoriesChipsList
                                  ids={interventionCategoriesIds}
                                  key={interventionCategoriesIds.length}
                                />
                              </div>
                            ) : (
                              <div className={styles.accessListItemField}>
                                <Chip
                                  type={ChipTypes.chipWhite}
                                  label={t('all-categories')}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      {!isDocumentationSubOptionLocked &&
                        subOption.fieldType ===
                          AccessesFieldType.AssetCategoriesSelect && (
                          <div>
                            {assetCategoriesIds?.length ? (
                              <div className={styles.accessListItemField}>
                                <AssetCategoriesChipsList
                                  ids={assetCategoriesIds}
                                  key={assetCategoriesIds.length}
                                />
                              </div>
                            ) : (
                              <div className={styles.accessListItemField}>
                                <Chip
                                  type={ChipTypes.chipWhite}
                                  label={t('all-categories')}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      {!isDocumentationSubOptionLocked &&
                        subOption.fieldType ===
                          AccessesFieldType.DocumentationCategoriesSelect && (
                          <div>
                            {documentationCategoriesIds?.length ? (
                              <div className={styles.accessListItemField}>
                                <DocumentationCategoriesChipsList
                                  ids={documentationCategoriesIds}
                                  key={documentationCategoriesIds.length}
                                />
                              </div>
                            ) : (
                              <div className={styles.accessListItemField}>
                                <Chip
                                  type={ChipTypes.chipWhite}
                                  label={t('all-categories')}
                                />
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
          </div>
        );
      })}
    </div>
  );
}

export default memo(TeamAccessView);
